import React, {
  FC,
  ChangeEventHandler,
  FormEventHandler,
  useState,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { LockIcon } from '@stpkg/stand-react';

import { ProductPurchaseServiceContext } from 'src/contexts/state-machines/purchase/ProductPurchaseServiceContext';
import { useVerifyReservationFlowProduct } from 'src/hooks/api/reservation-flow/useVerifyReservationFlowProduct';
import { useRevalidateGetReservationFlowProduct } from 'src/hooks/api/reservation-flow/useGetReservationFlowProduct';
import { useIsMobile } from 'src/reservation-page/hooks/helpers/useIsMobile';
import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSDetailHeaderButton } from 'src/reservation-page/detail-header/RSDetailHeaderButton';
import { RSFormMessageContainer } from 'src/reservation-page/forms/RSFormMessageContainer';
import { RSTextField } from 'src/reservation-page/forms/RSTextField';
import { RSButton } from 'src/reservation-page/RSButton';

import styles from '../../index.module.css';

export interface VerifyViewProps {
  merchantPublicId: string;
  productPublicId: number;
  onRequireClosing: () => void;
}

export const VerifyView: FC<VerifyViewProps> = ({
  merchantPublicId,
  productPublicId,
  onRequireClosing,
}) => {
  const intl = useIntl();

  const isMobile = useIsMobile();

  const [, send] = ProductPurchaseServiceContext.useActor();

  const { isValidating, verifyReservationFlowProduct } =
    useVerifyReservationFlowProduct({
      parameter: {
        merchant_public_id: merchantPublicId,
        product_public_id: productPublicId,
      },
    });
  const { revalidateGetReservationFlowProduct } =
    useRevalidateGetReservationFlowProduct({
      parameter: {
        merchant_public_id: merchantPublicId,
        product_public_id: productPublicId,
      },
    });

  const [inputPassword, setInputPassword] = useState('');
  const [inputPasswordError, setInputPasswordError] = useState<
    { message: string } | undefined
  >(undefined);

  const onPasswordInputChange = useCallback<
    ChangeEventHandler<HTMLInputElement>
  >((event) => {
    setInputPassword(event.target.value);
  }, []);

  const onFormSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (inputPassword.length === 0) return;

      const result = await verifyReservationFlowProduct({
        values: { password: inputPassword },
      });

      if ('error' in result) {
        setInputPasswordError(result.error);
        return;
      }

      await revalidateGetReservationFlowProduct();

      send({
        type: 'GOTO_INITIALIZING',
      });
    },
    [
      send,
      inputPassword,
      verifyReservationFlowProduct,
      revalidateGetReservationFlowProduct,
    ],
  );

  return (
    <>
      <div className="z-5 sticky top-0">
        <RSDetailHeader
          title={intl.formatMessage({
            defaultMessage: '月謝購入',
            id: 'jNg8P1',
            description:
              'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/index.tsx',
          })}
          leftButton={
            <RSDetailHeaderButton variant="close" onClick={onRequireClosing}>
              {intl.formatMessage({
                defaultMessage: '閉じる',
                id: 'y9fb2u',
                description:
                  'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/index.tsx',
              })}
            </RSDetailHeaderButton>
          }
        />
      </div>

      <div
        className={classnames(
          'overflow-y-auto',
          isMobile
            ? styles.mobileHeightRestriction
            : styles.pcHeightRestriction,
        )}
      >
        <div className="pc:pb-4 pc:pt-4 px-2.5 pb-6 pt-5">
          <div className="mb-6 flex flex-col items-center">
            <div className="mb-3">
              <LockIcon
                width="80"
                height="80"
                viewBox="0 0 48 48"
                className="fill-gray-1000"
              />
            </div>

            <div className="flex flex-col items-center">
              <div className="mb-1">
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                  {intl.formatMessage({
                    defaultMessage: 'パスワードが設定されています',
                    id: '246UxW',
                    description:
                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/index.tsx',
                  })}
                </div>
              </div>
              <div>
                <div className="rs-font text-13 text-gray-1000 pc:text-12 text-center font-normal">
                  {intl.formatMessage(
                    {
                      defaultMessage:
                        'このページを閲覧するには<br></br>パスワードを入力してください',
                      id: 'V5Y8uW',
                      description:
                        'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/index.tsx',
                    },
                    {
                      br: () => <br></br>,
                    },
                  )}
                </div>
              </div>
            </div>
          </div>

          <form
            id="subscriptionPurchaseModalVerifyForm"
            onSubmit={onFormSubmit}
          >
            <div className="mb-2.5">
              <RSFormMessageContainer
                isBlock
                errors={
                  typeof inputPasswordError !== 'undefined'
                    ? [inputPasswordError.message]
                    : []
                }
              >
                <RSTextField
                  labelText={intl.formatMessage({
                    defaultMessage: 'パスワード',
                    id: 't61tMl',
                    description:
                      'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/index.tsx',
                  })}
                  type="password"
                  isBlock
                  onChange={onPasswordInputChange}
                />
              </RSFormMessageContainer>
            </div>

            <div>
              <RSButton
                form="subscriptionPurchaseModalVerifyForm"
                variant="large"
                isBlock
                isLoading={isValidating}
                disabled={inputPassword.length === 0 || isValidating}
              >
                {intl.formatMessage({
                  defaultMessage: '次へ',
                  id: 'Byw9GM',
                  description:
                    'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/index.tsx',
                })}
              </RSButton>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
